import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../shared/services/api.service';
import { ThemeService } from '../../shared/services/theme.service';
import { formatSaMobile, normalizeSaMobile, saMobileValidator } from '../../shared/utils/sa-phone';

const STRONG_PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*[a-z])(?=.*[^A-Za-z0-9]).{8,}$/;

function passwordsMatchValidator(group: AbstractControl): ValidationErrors | null {
  const password = group.get('password')?.value;
  const confirmPassword = group.get('confirmPassword')?.value;
  if (!password || !confirmPassword) return null;
  return password === confirmPassword ? null : { passwordMismatch: true };
}

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.css'],
})
export class RegisterComponent implements OnInit {
  readonly industryOptions: Array<{ value: 'REAL_ESTATE_PROFESSIONAL' | 'LEGAL_PROFESSIONAL' | 'OTHER'; label: string }> =
    [
      { value: 'REAL_ESTATE_PROFESSIONAL', label: 'Real Estate Professional' },
      { value: 'LEGAL_PROFESSIONAL', label: 'Legal Professional' },
      { value: 'OTHER', label: 'Other' },
    ];
  readonly form: FormGroup;
  readonly termsUrl = 'https://thebigdatashowroom.com/terms';
  readonly privacyUrl = 'https://thebigdatashowroom.com/privacy';
  readonly signupTermsVersion = 'v1';
  readonly isDarkMode$ = this.themeService.isDarkMode$;
  loading = false;
  inviteMode = false;
  inviteRequiresCompany = false;
  inviteCompanyName = '';
  inviteToken: string | null = null;
  campaignMode = false;
  campaignToken: string | null = null;
  campaignName = '';
  reviewSubmitted = false;
  uploadedFileName = '';
  uploadStatusMessage = '';
  uploadedFfcFileName = '';
  ffcUploadStatusMessage = '';
  private selectedIdDocumentFile: File | null = null;
  private selectedFfcDocumentFile: File | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly api: ApiService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly snackBar: MatSnackBar,
    private readonly themeService: ThemeService
  ) {
    this.form = this.fb.group(
      {
        name: ['', [Validators.required, Validators.maxLength(100)]],
        surname: ['', [Validators.required, Validators.maxLength(100)]],
        companyName: [''],
        email: ['', [Validators.required, Validators.email]],
        phone: ['', [Validators.required, saMobileValidator]],
        idNumber: ['', [Validators.required, Validators.pattern(/^\d{13}$/)]],
        industry: ['', [Validators.required]],
        industryOther: [''],
        password: ['', [Validators.required, Validators.pattern(STRONG_PASSWORD_REGEX)]],
        confirmPassword: ['', [Validators.required]],
        idDocumentPath: [''],
        ffcDocumentPath: [''],
        signupTermsAccepted: [false, [Validators.requiredTrue]],
      },
      { validators: [passwordsMatchValidator] }
    );
  }

  toggleTheme(): void {
    this.themeService.toggleDarkMode();
  }

  get passwordMismatch(): boolean {
    return !!this.form.errors?.['passwordMismatch'];
  }

  onPhoneBlur(): void {
    const control = this.form.get('phone');
    const normalized = normalizeSaMobile(String(control?.value || ''));
    if (!normalized) return;
    control?.setValue(formatSaMobile(normalized), { emitEvent: false });
  }

  ngOnInit(): void {
    const companyControl = this.form.get('companyName');
    companyControl?.setValidators([Validators.required, Validators.maxLength(150)]);
    companyControl?.updateValueAndValidity({ emitEvent: false });
    this.configureIndustryValidators();
    this.form.get('industry')?.valueChanges.subscribe(() => {
      this.configureIndustryValidators();
    });

    const campaign = String(this.route.snapshot.queryParamMap.get('campaign') || '').trim();
    const token = String(this.route.snapshot.queryParamMap.get('token') || '').trim();
    const action = String(this.route.snapshot.queryParamMap.get('action') || '').trim();
    if (campaign) {
      this.campaignMode = true;
      this.campaignToken = campaign;
      this.form.get('email')?.enable({ emitEvent: false });
      void this.hydrateCampaignFromToken(campaign);
      return;
    }
    if (!token || action !== 'signup') {
      this.form.get('email')?.enable({ emitEvent: false });
      this.inviteRequiresCompany = true;
      return;
    }
    this.inviteMode = true;
    this.inviteToken = token;
    this.form.get('email')?.disable({ emitEvent: false });
    void this.hydrateInviteFromToken(token);
  }

  get isOtherIndustrySelected(): boolean {
    return this.form.get('industry')?.value === 'OTHER';
  }

  get isRealEstateIndustrySelected(): boolean {
    return this.form.get('industry')?.value === 'REAL_ESTATE_PROFESSIONAL';
  }

  private configureIndustryValidators(): void {
    const industryValue = String(this.form.get('industry')?.value || '').trim();
    const industryOtherControl = this.form.get('industryOther');
    const ffcDocumentPathControl = this.form.get('ffcDocumentPath');

    if (industryValue === 'OTHER') {
      industryOtherControl?.setValidators([Validators.required, Validators.maxLength(150)]);
    } else {
      industryOtherControl?.clearValidators();
      industryOtherControl?.setValue('', { emitEvent: false });
    }
    industryOtherControl?.updateValueAndValidity({ emitEvent: false });

    if (industryValue === 'REAL_ESTATE_PROFESSIONAL') {
      ffcDocumentPathControl?.setValidators([Validators.required]);
    } else {
      ffcDocumentPathControl?.clearValidators();
      ffcDocumentPathControl?.setValue('', { emitEvent: false });
      this.selectedFfcDocumentFile = null;
      this.uploadedFfcFileName = '';
      this.ffcUploadStatusMessage = '';
    }
    ffcDocumentPathControl?.updateValueAndValidity({ emitEvent: false });
  }

  private async hydrateCampaignFromToken(token: string): Promise<void> {
    this.loading = true;
    try {
      const verified = await firstValueFrom(this.api.verifyPublicCampaign(token));
      this.campaignName = String(verified.campaign?.name || '').trim();
      const orgName = String(verified.campaign?.orgDisplayName || '').trim();
      this.inviteCompanyName = orgName;
      this.inviteRequiresCompany = false;
      const companyControl = this.form.get('companyName');
      companyControl?.clearValidators();
      companyControl?.setValue(orgName, { emitEvent: false });
      companyControl?.disable({ emitEvent: false });
      companyControl?.updateValueAndValidity({ emitEvent: false });
    } catch (error: any) {
      const message =
        error?.error?.error?.message || 'This campaign link is invalid, expired, or no longer available';
      this.snackBar.open(message, 'Close', { duration: 8000 });
      await this.router.navigate(['/login']);
    } finally {
      this.loading = false;
    }
  }

  private async hydrateInviteFromToken(token: string): Promise<void> {
    this.loading = true;
    try {
      const verified = await firstValueFrom(this.api.verifyPasswordActionToken(token));
      if (verified.action !== 'signup') {
        throw new Error('Invalid signup token');
      }
      this.inviteRequiresCompany = verified.team === false;
      this.inviteCompanyName = String(verified.companyName || '').trim();
      const companyControl = this.form.get('companyName');
      if (this.inviteRequiresCompany) {
        companyControl?.setValidators([Validators.required, Validators.maxLength(150)]);
        companyControl?.enable({ emitEvent: false });
      } else {
        companyControl?.clearValidators();
        companyControl?.disable({ emitEvent: false });
      }
      companyControl?.updateValueAndValidity({ emitEvent: false });
      this.form.patchValue(
        {
          email: verified.email || '',
          name: verified.name || '',
          surname: verified.surname || '',
          companyName: verified.companyName || '',
        },
        { emitEvent: false }
      );
    } catch (_error) {
      this.snackBar.open('This invite link is invalid or expired. Please request a new invite', 'Close', {
        duration: 8000,
      });
      await this.router.navigate(['/login']);
    } finally {
      this.loading = false;
    }
  }

  onIdDocumentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0] || null;
    this.selectedIdDocumentFile = file;
    this.uploadedFileName = file?.name || '';
    this.uploadStatusMessage = file ? 'File selected successfully' : '';
    if (!file) {
      return;
    }
    this.form.patchValue({ idDocumentPath: file.name }, { emitEvent: false });
  }

  onFfcDocumentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0] || null;
    this.selectedFfcDocumentFile = file;
    this.uploadedFfcFileName = file?.name || '';
    this.ffcUploadStatusMessage = file ? 'File selected successfully' : '';
    if (!file) {
      return;
    }
    this.form.patchValue({ ffcDocumentPath: file.name }, { emitEvent: false });
  }

  private async toBase64(file: File): Promise<string> {
    const arrayBuffer = await file.arrayBuffer();
    let binary = '';
    const bytes = new Uint8Array(arrayBuffer);
    for (const byte of bytes) {
      binary += String.fromCharCode(byte);
    }
    return btoa(binary);
  }

  private handleRegistrationOutcome(approvalStatus: string | undefined): void {
    if (approvalStatus === 'PENDING') {
      this.reviewSubmitted = true;
      return;
    }
    this.snackBar.open('Registration complete. You can now login.', 'Close', { duration: 6000 });
    void this.router.navigate(['/login']);
  }

  openExternalLink(event: MouseEvent, url: string): void {
    event.preventDefault();
    event.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  async submit(): Promise<void> {
    if (this.form.invalid || this.loading) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading = true;
    const payload = this.form.getRawValue();
    const normalizedPhone = normalizeSaMobile(String(payload.phone || ''));
    if (!normalizedPhone) {
      this.form.get('phone')?.setErrors({ saMobile: true });
      this.form.markAllAsTouched();
      this.loading = false;
      return;
    }
    const registerPayload = {
      name: payload.name,
      surname: payload.surname,
      companyName: payload.companyName,
      email: payload.email,
      phone: normalizedPhone,
      idNumber: payload.idNumber,
      industry: payload.industry,
      industryOther: payload.industryOther,
      password: payload.password,
      idDocumentPath: payload.idDocumentPath,
      ffcDocumentPath: payload.ffcDocumentPath,
      signupTermsAccepted: payload.signupTermsAccepted === true,
      signupTermsVersion: this.signupTermsVersion,
    };
    if (!registerPayload.signupTermsAccepted) {
      this.snackBar.open('Please accept the Terms of Use and Privacy & POPIA Notice', 'Close', {
        duration: 6000,
      });
      this.loading = false;
      return;
    }
    if (!this.selectedIdDocumentFile) {
      this.snackBar.open('Please attach an ID document before submitting', 'Close', { duration: 6000 });
      this.loading = false;
      return;
    }
    if (this.isRealEstateIndustrySelected && !this.selectedFfcDocumentFile) {
      this.snackBar.open('Please attach an FFC document before submitting', 'Close', { duration: 6000 });
      this.loading = false;
      return;
    }

    let idDocumentBase64 = '';
    let ffcDocumentBase64: string | undefined;
    try {
      idDocumentBase64 = await this.toBase64(this.selectedIdDocumentFile);
      if (this.isRealEstateIndustrySelected && this.selectedFfcDocumentFile) {
        ffcDocumentBase64 = await this.toBase64(this.selectedFfcDocumentFile);
      }
    } catch {
      this.loading = false;
      this.snackBar.open('Failed to read one or more document files', 'Close', { duration: 6000 });
      return;
    }

    if (this.inviteMode && this.inviteToken) {
      this.api
        .consumePasswordActionToken({
          token: this.inviteToken,
          action: 'signup',
          name: registerPayload.name,
          surname: registerPayload.surname,
          companyName: registerPayload.companyName,
          phone: registerPayload.phone,
          idNumber: registerPayload.idNumber,
          industry: registerPayload.industry,
          industryOther: registerPayload.industryOther,
          password: registerPayload.password,
          idDocumentBase64,
          idDocumentMimeType: this.selectedIdDocumentFile?.type || 'application/octet-stream',
          idDocumentFileName: this.selectedIdDocumentFile?.name || 'id-document',
          ffcDocumentBase64,
          ffcDocumentMimeType: this.selectedFfcDocumentFile?.type || undefined,
          ffcDocumentFileName: this.selectedFfcDocumentFile?.name || undefined,
          signupTermsAccepted: true,
          signupTermsVersion: this.signupTermsVersion,
        })
        .subscribe({
          next: (response) => {
            this.handleRegistrationOutcome(response.approvalStatus);
          },
          error: (error) => {
            this.loading = false;
            const message = error?.error?.error?.message || 'Failed to complete signup';
            this.snackBar.open(message, 'Close', { duration: 6000 });
          },
          complete: () => {
            this.loading = false;
          },
        });
      return;
    }

    this.api
      .register({
        ...registerPayload,
        companyName: this.campaignMode
          ? String(this.form.getRawValue().companyName || this.inviteCompanyName || registerPayload.companyName || '').trim()
          : registerPayload.companyName,
        campaign: this.campaignMode && this.campaignToken ? this.campaignToken : undefined,
        idDocumentBase64,
        idDocumentMimeType: this.selectedIdDocumentFile?.type || 'application/octet-stream',
        idDocumentFileName: this.selectedIdDocumentFile?.name || 'id-document',
        ffcDocumentBase64,
        ffcDocumentMimeType: this.selectedFfcDocumentFile?.type || undefined,
        ffcDocumentFileName: this.selectedFfcDocumentFile?.name || undefined,
      })
      .subscribe({
        next: (response) => {
          this.handleRegistrationOutcome(response.approvalStatus);
        },
        error: (error) => {
          this.loading = false;
          const message = error?.error?.error?.message || 'Registration failed';
          this.snackBar.open(message, 'Close', { duration: 6000 });
        },
        complete: () => {
          this.loading = false;
        },
      });
  }
}
